// §4.3 — OFF-CUT DISCOUNT CLASSES, /sales/offcut-classes (Module 09, slice
// P3-A part 2). The §2.10 catalog editor: how much a customer is discounted
// for taking a cable order as off-cut PIECES instead of a fresh run, and
// whether that discount is allowed to walk past the §2.11 margin guard.
//
// A BOUNDED DEFINITION SET, so no cursor plumbing: the read is unpaginated,
// exactly as the module-05/07 catalogs are, and a page-walk control here would
// be inventing an affordance the server does not offer.
//
// NAMES ARE A LOCALE BUNDLE (D-209): `name_i18n` carries one string per
// locale, the wire keeps a row-local English `name` projection, and the table
// renders the READER'S locale through `localeLabel`. So the editor has one
// name field PER LOCALE with `en` required — the waste category-editor shape,
// for the same reason: a class named only in English is a class a Russian
// sales manager reads in English forever.
//
// WHO MAY WRITE. §7's "Margin guard / off-cut class edit" cell is group_admin
// ALONE, and the service enforces exactly that. The client mirror cannot see
// the difference between ga and fa on the `sales.tier.manage` code they both
// hold, so the controls are offered to tier.manage holders WITH the note that
// the server admits group_admin only — a refusal rendered as the server's own
// sentence (MutationFailureNote) beats a control silently missing for the one
// role that actually holds it (D-118's rule, applied in the offering
// direction).
//
// THE RATE FOLLOWS THE VALUE GRANT (D-337 ruling 6). `discount_pct` is served
// only to a holder of `sales.read_value`, because with a product's list price
// it rebuilds the unit price of every off-cut line. The list itself stays open
// to every `sales.read` holder, since the composer names a line's class, so a
// reader without the grant sees each class with the rate cell saying the
// figure is not shown to their role. Such a reader is offered no edit either:
// the form seeds the rate it would save, and a rate typed blind would
// overwrite a figure the reader was never given. Only a tenant's custom role
// meets this; every system role that reaches this screen holds the grant.
//
// EMPTY STATE NAMES THE SEEDS. The vertical pack authors OFFCUT_SINGLE (1
// piece, 20 %) and OFFCUT_COMBO2 (2 pieces, 30 %); an empty catalog on a
// seeded tenant means the pack was not installed, and the copy says which two
// codes should have been there rather than "nothing here yet".

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesGroupTabs } from "../components/group-tabs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CheckboxField, Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { RefLabel } from "../../../../components/ref-label";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import type { Locale } from "@assessly/contracts/locales";
import {
  bundleText,
  emptyLocaleDraft,
  localeBundleOf,
  localeDraftOf,
  otherLocales,
  primaryLocales,
} from "../../../../lib/locale-bundle";
import { LOCALE_ENDONYMS } from "../../../../lib/locale";
import { MutationFailureNote } from "../mutation-failure-note";
import { isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import {
  useCreateOffcutClass,
  useOffcutClasses,
  useUpdateOffcutClass,
  type OffcutClass,
  type OffcutClassCreateRequest,
} from "../hooks/sales-queries";
import { bookClientPager } from "../hooks/book-pages";

/** One name field per product locale (D-430). `en` is required by the
 * contract's `SalesLocaleName`; every other locale is optional and omitted
 * when left blank rather than saved as an empty string. */
type NameDraft = Record<Locale, string>;

/** `{en, ...}` from the draft, blanks OMITTED, never sent as "". */
export function nameBundleOf(draft: NameDraft): OffcutClassCreateRequest["name_i18n"] {
  return localeBundleOf(draft);
}

/** The §2.10 piece window as one cell ("1–1", "2–4"). */
export function pieceRange(row: Pick<OffcutClass, "min_pieces" | "max_pieces">): string {
  return row.min_pieces === row.max_pieces
    ? String(row.min_pieces)
    : `${row.min_pieces}–${row.max_pieces}`;
}

const CODE = /^[A-Z][A-Z0-9_]{1,47}$/u;
const PCT = /^(100(\.00?)?|\d{1,2}(\.\d{1,2})?)$/u;
const LENGTH = /^(?=.*[1-9])\d{1,11}(\.\d{1,3})?$/u;

export function SalesOffcutClassesScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();

  const refused = isSalesReadRefused(perms, perms.canOpenPricing && perms.has("sales.read"));
  const classes = useOffcutClasses(!refused);
  const canManage = perms.has("sales.tier.manage");

  const create = useCreateOffcutClass();
  const update = useUpdateOffcutClass();
  const [editing, setEditing] = useState<OffcutClass | "new" | undefined>(undefined);

  useEffect(() => {
    if (classes.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [classes.error, navigate]);

  const rows = classes.data ?? [];
  // Some class came without its rate, so its edit is withheld and the page
  // says why rather than leaving the Edit button silently missing (D-118).
  const ratesWithheld = rows.some((row) => row.discount_pct === undefined);

  const columns: DataTableColumn<OffcutClass>[] = [
    { key: "code", header: t("sales.offcut.colCode"), id: true },
    {
      key: "name",
      header: t("sales.offcut.colName"),
      render: (row) => bundleText(row.name_i18n, i18n.language) || row.name,
    },
    {
      key: "pieces",
      header: t("sales.offcut.colPieces"),
      numeric: true,
      render: (row) => pieceRange(row),
    },
    {
      key: "min_piece_length",
      header: t("sales.offcut.colMinLength"),
      numeric: true,
      // §2.10 allows both bounds to be null (a class with no length window).
      // The register's absent mark says so; an empty cell would read as a
      // value the screen failed to paint.
      render: (row) => <RefLabel value={row.min_piece_length ?? undefined} id={null} />,
    },
    {
      key: "max_piece_length",
      header: t("sales.offcut.colMaxLength"),
      numeric: true,
      render: (row) => <RefLabel value={row.max_piece_length ?? undefined} id={null} />,
    },
    {
      key: "discount_pct",
      header: t("sales.offcut.colDiscount"),
      numeric: true,
      render: (row) =>
        row.discount_pct === undefined ? (
          <StatusChip variant="neutral" dot={false} data-testid="sales-offcut-pct-redacted">
            {t("sales.overrides.redacted")}
          </StatusChip>
        ) : (
          t("sales.offcut.pct", { pct: row.discount_pct })
        ),
    },
    {
      key: "bypasses_margin_guard",
      header: t("sales.offcut.colBypass"),
      render: (row) => (
        <StatusChip
          variant={row.bypasses_margin_guard ? "warn" : "neutral"}
          data-testid="sales-offcut-bypass"
        >
          {row.bypasses_margin_guard ? t("sales.offcut.bypasses") : t("sales.offcut.guarded")}
        </StatusChip>
      ),
    },
    {
      key: "status",
      header: t("sales.offcut.colStatus"),
      render: (row) => (
        <StatusChip variant={row.status === "active" ? "ok" : "neutral"}>
          {row.status === "active"
            ? t("sales.customers.statusActive")
            : t("sales.customers.statusArchived")}
        </StatusChip>
      ),
    },
    {
      key: "actions",
      header: t("sales.offcut.colActions"),
      actions: true,
      render: (row) =>
        canManage && row.discount_pct !== undefined ? (
          <Button
            size="sm"
            data-testid="sales-offcut-edit"
            onClick={() => {
              create.reset();
              update.reset();
              setEditing(row);
            }}
          >
            {t("sales.common.edit")}
          </Button>
        ) : null,
    },
  ];

  return (
    <section className="sales-offcut-screen" data-testid="sales-offcut-screen">
      <PageHeader
        title={t("sales.offcut.title")}
        actions={
          <>
            {canManage ? (
              <Button
                variant="primary"
                data-testid="sales-offcut-new"
                onClick={() => {
                  create.reset();
                  update.reset();
                  setEditing("new");
                }}
              >
                {t("sales.offcut.new")}
              </Button>
            ) : null}
          </>
        }
      />
      <SalesGroupTabs group="settings" />

      {/* §7: the edit cell is group_admin ALONE, and the mirror cannot tell ga
          from fa on the shared code. Said out loud rather than discovered as a
          403 after typing a form. */}
      {canManage ? (
        <p className="sales-pending-note" data-testid="sales-offcut-ga-note">
          {t("sales.offcut.gaNote")}
        </p>
      ) : (
        <p className="sales-pending-note" data-testid="sales-offcut-readonly">
          {t("sales.offcut.readOnly")}
        </p>
      )}

      {canManage && ratesWithheld ? (
        <p className="sales-pending-note" data-testid="sales-offcut-rates-hidden">
          {t("sales.offcut.ratesHidden")}
        </p>
      ) : null}

      {refused ? (
        <ForbiddenState title={t("sales.access.title")} body={t("sales.access.pricing")} />
      ) : null}

      {!refused && classes.fetchStatus === "paused" && classes.data === undefined ? (
        <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
      ) : null}

      {!refused && classes.isPending && classes.fetchStatus !== "paused" ? (
        <TableSkeleton rows={4} cols={columns.length} label={t("sales.common.loading")} />
      ) : null}

      {!refused && classes.error !== null && !(classes.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("sales.offcut.error")}
          retryLabel={t("sales.common.retry")}
          requestIdLabel={t("sales.common.requestId")}
          requestId={requestIdOf(classes.error)}
          onRetry={() => {
            void classes.refetch();
          }}
        />
      ) : null}

      {!refused &&
      !classes.isPending &&
      classes.fetchStatus !== "paused" &&
      classes.error === null &&
      rows.length === 0 ? (
        <EmptyState title={t("sales.offcut.emptyTitle")} body={t("sales.offcut.emptyBody")} />
      ) : null}

      {create.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.offcut.createFailed")}
          error={create.error}
        />
      ) : null}
      {update.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.offcut.updateFailed")}
          error={update.error}
        />
      ) : null}

      {rows.length > 0 ? (
        <DataTable
          clientPager={bookClientPager(t, i18n.language)}
          data-testid="sales-offcut-table"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          empty={null}
          caption={t("sales.offcut.tableCaption")}
          rowTestId="sales-offcut-row"
          rowProps={(row) => ({ "data-code": row.code, "data-status": row.status })}
        />
      ) : null}

      {editing !== undefined ? (
        <OffcutClassModal
          row={editing === "new" ? undefined : editing}
          busy={create.isPending || update.isPending}
          onClose={() => {
            setEditing(undefined);
          }}
          onCreate={(body) => {
            update.reset();
            create.mutate(body, {
              onSuccess: () => {
                setEditing(undefined);
              },
            });
          }}
          onUpdate={(id, body) => {
            create.reset();
            update.mutate(
              { id, body },
              {
                onSuccess: () => {
                  setEditing(undefined);
                },
              },
            );
          }}
        />
      ) : null}
    </section>
  );
}

function OffcutClassModal({
  row,
  busy,
  onClose,
  onCreate,
  onUpdate,
}: {
  row: OffcutClass | undefined;
  busy: boolean;
  onClose: () => void;
  onCreate: (body: OffcutClassCreateRequest) => void;
  onUpdate: (
    id: string,
    body: {
      name_i18n?: OffcutClassCreateRequest["name_i18n"];
      min_pieces?: number;
      max_pieces?: number;
      min_piece_length?: string | null;
      max_piece_length?: string | null;
      discount_pct?: string;
      bypasses_margin_guard?: boolean;
      status?: "active" | "archived";
    },
  ) => void;
}) {
  const { t, i18n } = useTranslation();
  const [code, setCode] = useState(row?.code ?? "");
  const [names, setNames] = useState<NameDraft>(() =>
    row === undefined ? emptyLocaleDraft() : localeDraftOf(row.name_i18n),
  );
  const nameField = (locale: Locale) => (
    <Field
      key={locale}
      label={t("sales.offcut.nameIn", { language: LOCALE_ENDONYMS[locale] })}
      wide
    >
      <input
        value={names[locale]}
        lang={locale}
        data-testid={`sales-offcut-name-${locale}`}
        onChange={(e) => {
          setNames((prior) => ({ ...prior, [locale]: e.target.value }));
        }}
      />
    </Field>
  );
  const [minPieces, setMinPieces] = useState(String(row?.min_pieces ?? 1));
  const [maxPieces, setMaxPieces] = useState(String(row?.max_pieces ?? 1));
  const [minLength, setMinLength] = useState(row?.min_piece_length ?? "");
  const [maxLength, setMaxLength] = useState(row?.max_piece_length ?? "");
  const [discount, setDiscount] = useState(row?.discount_pct ?? "");
  const [bypass, setBypass] = useState(row?.bypasses_margin_guard ?? false);
  const [archived, setArchived] = useState(row?.status === "archived");

  const codeClean = code.trim().toUpperCase();
  const minN = Number(minPieces);
  const maxN = Number(maxPieces);
  const lengthsOk =
    (minLength.trim().length === 0 || LENGTH.test(minLength.trim())) &&
    (maxLength.trim().length === 0 || LENGTH.test(maxLength.trim()));
  const valid =
    (row !== undefined || CODE.test(codeClean)) &&
    names.en.trim().length > 0 &&
    Number.isInteger(minN) &&
    minN >= 1 &&
    Number.isInteger(maxN) &&
    maxN >= minN &&
    PCT.test(discount.trim()) &&
    lengthsOk;

  return (
    <Modal
      heading={row === undefined ? t("sales.offcut.createTitle") : t("sales.offcut.editTitle")}
      label={row === undefined ? t("sales.offcut.createTitle") : t("sales.offcut.editTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-offcut-modal"
    >
      <div className="sales-form">
        {row === undefined ? (
          <Field label={t("sales.offcut.code")}>
            <input
              value={code}
              placeholder={t("sales.offcut.codeHint")}
              data-testid="sales-offcut-code"
              onChange={(e) => {
                setCode(e.target.value);
              }}
            />
          </Field>
        ) : null}
        {/* The source locale and the reader's own are the two names a
            writer types; the other languages wait behind one disclosure
            (D-446), so a six-language product does not draw six fields. */}
        {primaryLocales(i18n.language).map((locale) => nameField(locale))}
        <details className="sales-other-languages" data-testid="sales-offcut-other-languages">
          <summary>{t("sales.common.otherLanguages")}</summary>
          <div className="sales-other-languages-fields">
            {otherLocales(i18n.language).map((locale) => nameField(locale))}
          </div>
        </details>
        <Field label={t("sales.offcut.minPieces")}>
          <input
            inputMode="numeric"
            value={minPieces}
            data-testid="sales-offcut-min-pieces"
            onChange={(e) => {
              setMinPieces(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.offcut.maxPieces")}>
          <input
            inputMode="numeric"
            value={maxPieces}
            data-testid="sales-offcut-max-pieces"
            onChange={(e) => {
              setMaxPieces(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.offcut.minLength")}>
          <input
            inputMode="decimal"
            value={minLength}
            onChange={(e) => {
              setMinLength(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.offcut.maxLength")}>
          <input
            inputMode="decimal"
            value={maxLength}
            onChange={(e) => {
              setMaxLength(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.offcut.discount")}>
          <input
            inputMode="decimal"
            value={discount}
            data-testid="sales-offcut-discount"
            onChange={(e) => {
              setDiscount(e.target.value);
            }}
          />
        </Field>
        <CheckboxField
          label={t("sales.offcut.bypassLabel")}
          checked={bypass}
          data-testid="sales-offcut-bypass-toggle"
          onChange={setBypass}
        />
        {row !== undefined ? (
          <CheckboxField
            label={t("sales.offcut.archivedLabel")}
            checked={archived}
            data-testid="sales-offcut-archived-toggle"
            onChange={setArchived}
          />
        ) : null}
      </div>
      <p className="sales-hint">{t("sales.offcut.bypassHint")}</p>
      <div className="sales-modal-actions">
        <Button onClick={onClose}>{t("sales.common.cancel")}</Button>
        <Button
          variant="primary"
          data-testid="sales-offcut-submit"
          busy={busy}
          busyLabel={t("sales.common.saving")}
          disabled={!valid}
          onClick={() => {
            const bundle = nameBundleOf(names);
            if (row === undefined) {
              onCreate({
                code: codeClean,
                name_i18n: bundle,
                min_pieces: minN,
                max_pieces: maxN,
                ...(minLength.trim().length > 0 ? { min_piece_length: minLength.trim() } : {}),
                ...(maxLength.trim().length > 0 ? { max_piece_length: maxLength.trim() } : {}),
                discount_pct: discount.trim(),
                bypasses_margin_guard: bypass,
              });
              return;
            }
            onUpdate(row.id, {
              name_i18n: bundle,
              min_pieces: minN,
              max_pieces: maxN,
              min_piece_length: minLength.trim().length > 0 ? minLength.trim() : null,
              max_piece_length: maxLength.trim().length > 0 ? maxLength.trim() : null,
              discount_pct: discount.trim(),
              bypasses_margin_guard: bypass,
              status: archived ? "archived" : "active",
            });
          }}
        >
          {t("sales.common.save")}
        </Button>
      </div>
    </Modal>
  );
}
